import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { API } from '@/utils/api.utils';
import api from '@/utils/api.utils';
import { UniversityResponse, ProfileStrength, Deadline, ApplicationProgress, RequiredDocument } from '../types';

export const useApplicationData = () => {
  const { data: universitiesData, isLoading, error } = useQuery<UniversityResponse>({
    queryKey: ['user-universities'],
    queryFn: async () => {
      const response = await api.get(API.USER_UNIVERSITIES);
      return response.data;
    },
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  });

  const profileStrength: ProfileStrength = useMemo(() => {
    return {
      overall: 85,
      personalInfo: 100,
      testScores: 100,
      activities: 80,
      essays: 60,
      recommendations: 33,
    };
  }, []);

  const upcomingDeadlines: Deadline[] = useMemo(() => {
    return [
      {
        date: '15',
        month: 'JAN',
        title: 'MIT Early Action',
        description: 'Application & Supplemental Essays Due',
        daysLeft: 5,
        completion: 75,
        urgent: true,
      },
      {
        date: '20',
        month: 'JAN',
        title: 'Harvard Regular Decision',
        description: 'All Materials & Recommendations Due',
        daysLeft: 10,
        completion: 60,
        urgent: false,
      },
      {
        date: '01',
        month: 'FEB',
        title: 'UC Berkeley',
        description: 'Personal Insight Questions Due',
        daysLeft: 22,
        completion: 40,
        urgent: false,
      },
    ];
  }, []);

  const applicationProgress: ApplicationProgress[] = useMemo(() => {
    if (!universitiesData) return [];
    
    const allUniversities = [
      ...universitiesData.IN_PROGRESS.universities,
      ...universitiesData.PENDING.universities,
      ...universitiesData.COMPLETED.universities,
    ];

    return allUniversities.map((uni) => ({
      id: uni.id,
      name: uni.university_name,
      status: uni.status,
      deadline: 'Jan 5, 2025',
      completion: uni.status === 'COMPLETED' ? 100 : uni.status === 'IN_PROGRESS' ? 85 : 40,
    }));
  }, [universitiesData]);

  const requiredDocuments: RequiredDocument[] = useMemo(() => [
    {
      name: 'Official Transcript',
      uploaded: true,
      date: 'Dec 10, 2024',
      size: '2.4 MB',
    },
    {
      name: 'SAT Score Report',
      uploaded: true,
      date: 'Dec 12, 2024',
      size: '1.1 MB',
    },
    {
      name: 'ACT Score Report',
      uploaded: true,
      date: 'Dec 12, 2024',
      size: '987 KB',
    },
    {
      name: 'Mid-Year Report',
      uploaded: false,
      requiredBy: 'Feb 1, 2025',
    },
    {
      name: 'Financial Aid Documents',
      uploaded: false,
      optional: true,
    },
  ], []);

  return {
    universitiesData,
    isLoading,
    error,
    profileStrength,
    upcomingDeadlines,
    applicationProgress,
    requiredDocuments,
  };
};